import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { getFindings } from '../../lib/services/findings'
import { getReports } from '../../lib/services/reports'
import { canAccessReviewerWorkbench } from '../../lib/services/reviewerWorkbench'

export function ReviewerQueuePage() {
  const { viewerContext } = useAuth()
  const role = viewerContext?.role || 'Surveyor'
  const canAccess = canAccessReviewerWorkbench(role)

  const findingsQuery = useQuery({
    queryKey: ['reviewer-queue-findings'],
    queryFn: () => getFindings({ status: 'draft' }),
    enabled: canAccess,
  })

  const reportsQuery = useQuery({
    queryKey: ['reviewer-queue-reports'],
    queryFn: getReports,
    enabled: canAccess,
  })

  if (!canAccess) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Reviewer workbench is restricted to Reviewer/Admin roles.
      </div>
    )
  }

  const reports = (reportsQuery.data || []).filter((report) => report.status === 'draft')
  const findings = findingsQuery.data || []

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Reviewer Workbench</h1>
        <p className="mt-1 text-sm text-slate-600">Queue for draft findings and draft reports pending review/release.</p>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Draft Findings Queue</h2>
        {findingsQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading findings...</p>}
        {findingsQuery.error && <p className="mt-2 text-sm text-red-600">{(findingsQuery.error as Error).message}</p>}
        {!findingsQuery.isLoading && !findingsQuery.error && findings.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No draft findings in queue.</p>
        )}
        <div className="mt-3 grid gap-3">
          {findings.map((finding) => (
            <Link
              key={finding.finding_id}
              to={`/reviewer/finding/${finding.finding_id}`}
              className="v11-dashboard-card p-4"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-900">Standard {finding.standard_id}</p>
                <span className="badge-in-progress">draft</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">Severity: {finding.severity}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Draft Reports Queue</h2>
        {reportsQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading reports...</p>}
        {reportsQuery.error && <p className="mt-2 text-sm text-red-600">{(reportsQuery.error as Error).message}</p>}
        {!reportsQuery.isLoading && !reportsQuery.error && reports.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No draft reports in queue.</p>
        )}
        <div className="mt-3 grid gap-3">
          {reports.map((report) => (
            <Link
              key={report.report_id}
              to={`/reviewer/report/${report.report_id}`}
              className="v11-dashboard-card p-4"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-900">Report {report.report_id}</p>
                <span className="badge-in-progress">{report.status}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">Visit: {report.visit_id} | Version: {report.version}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}
